/**
 * Feature flags nos apps. A resolução (alvo de clube/usuário, rollout) é feita no
 * SERVIDOR pela Edge Function get-feature-flags, com a mesma regra que as Edge Functions
 * usam (supabase/functions/_shared/feature-flags.ts). O app só recebe ligada/desligada.
 *
 * Uso (uma vez por app, abaixo do QueryClientProvider):
 *
 *   <FeatureFlagsProvider client={supabase} userId={user?.id ?? null} clubId={club?.id ?? null}>
 *     <App />
 *   </FeatureFlagsProvider>
 *
 *   const ligada = useFeatureFlag("crm.cobranca.cobrar_em_atraso");
 *   <ComFlag chave="crm.cobranca.cobrar_em_atraso"><BotaoNovo /></ComFlag>
 *
 * FALHA FECHADA: enquanto carrega, sem provider, em erro ou flag inexistente → desligada.
 * Funcionalidade nova atrás de flag nunca aparece por acidente. Erro vai ao Sentry.
 *
 * `clubId` = clube em que o usuário está agindo (no CRM, o clube ativo). O servidor valida
 * a relação (dono, staff, membro aprovado, admin); é o que faz "liberar só para o clube X"
 * valer para a equipe dele. Sem login (portal), só valem flags ligadas para todos.
 */
import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { qk } from "@pps/query-keys";
import { Sentry } from "@pps/observability";

export type Flags = Readonly<Record<string, boolean>>;

// Mesma referência sempre: `{}` novo a cada render invalidaria memos de quem depende das flags.
const VAZIO: Flags = Object.freeze({});

/** Status HTTP de um erro de functions.invoke (FunctionsHttpError traz a Response). */
function statusDoErro(erro: unknown): number | undefined {
  const contexto = (erro as { context?: { status?: unknown } } | null)?.context;
  return typeof contexto?.status === "number" ? contexto.status : undefined;
}

// Só o necessário: qualquer SupabaseClient do app (tipado com o Database ou não) serve.
type ClienteDeFuncoes = Pick<SupabaseClient, "functions">;

interface Contexto {
  client: ClienteDeFuncoes;
  userId: string | null;
  clubId: string | null;
}

const FeatureFlagsContext = createContext<Contexto | null>(null);

export interface FeatureFlagsProviderProps {
  children: ReactNode;
  client: ClienteDeFuncoes;
  userId: string | null;
  clubId?: string | null;
}

export function FeatureFlagsProvider({ children, client, userId, clubId = null }: FeatureFlagsProviderProps) {
  const valor = useMemo(
    () => ({ client, userId, clubId: clubId || null }),
    [client, userId, clubId],
  );
  return <FeatureFlagsContext.Provider value={valor}>{children}</FeatureFlagsContext.Provider>;
}

/** Caminho da Edge Function com o clube, se houver (puro; testado). */
export function caminhoDaConsulta(clubId: string | null): string {
  return clubId ? `get-feature-flags?club_id=${encodeURIComponent(clubId)}` : "get-feature-flags";
}

/** Leitura falha-fechada (puro; testado). */
export function lerFlag(flags: Flags | undefined, chave: string): boolean {
  return flags?.[chave] === true;
}

export interface EstadoDasFlags {
  flags: Flags;
  carregando: boolean;
  erro: unknown;
  /**
   * Já houve UMA resposta do servidor (as `flags` vêm dela, não do vazio). Num
   * refetch que falha, o React Query mantém a última resposta boa com `erro`
   * preenchido: quem quer falhar fechado só quando NÃO há resposta nenhuma
   * olha `erro && !temDados`.
   */
  temDados: boolean;
}

export interface OpcoesDaFlag {
  /**
   * Clube de DESTINO da ação, quando não é o ativo (ex.: aula de clube-filho vista
   * com o pai ativo). Omitido = o clube do provider. A Edge get-feature-flags valida
   * a relação do usuário com ele; é a mesma resolução que a Edge Function da ação
   * faz pelo `club_id` do pedido (IR-RX-015).
   */
  clubId?: string | null;
}

/** Todas as flags resolvidas para quem está usando. Sem provider: nenhuma ligada. */
export function useFeatureFlags(opcoes: OpcoesDaFlag = {}): EstadoDasFlags {
  const ctx = useContext(FeatureFlagsContext);
  const clubId = opcoes.clubId === undefined ? (ctx?.clubId ?? null) : (opcoes.clubId || null);
  const consulta = useQuery({
    queryKey: qk.featureFlags.resolvidas(ctx?.userId, clubId),
    enabled: !!ctx,
    staleTime: 60_000,
    retry: 1,
    queryFn: async (): Promise<Flags> => {
      const { data, error } = await ctx!.client.functions.invoke<{ flags?: Record<string, boolean> }>(
        caminhoDaConsulta(clubId),
        { method: "GET" },
      );
      if (error) throw error;
      return data?.flags ?? VAZIO;
    },
  });
  // Sentry uma vez por falha FINAL (não a cada tentativa). 401/403 são esperados (sessão
  // expirada, clube sem relação): não são exceção — a flag só fica desligada.
  useEffect(() => {
    const erro = consulta.error;
    if (!erro) return;
    const status = statusDoErro(erro);
    if (status === 401 || status === 403) return;
    Sentry.captureException(erro, { tags: { feature: "feature-flags", action: "carregar" } });
  }, [consulta.error]);
  return {
    flags: consulta.data ?? VAZIO,
    carregando: consulta.isLoading,
    erro: consulta.error,
    temDados: consulta.data !== undefined,
  };
}

/** `true` só quando o servidor disse que está ligada para este usuário/clube. */
export function useFeatureFlag(chave: string, opcoes: OpcoesDaFlag = {}): boolean {
  return lerFlag(useFeatureFlags(opcoes).flags, chave);
}

export interface ComFlagProps {
  chave: string;
  children: ReactNode;
  /** O que mostrar com a flag desligada (padrão: nada). */
  alternativa?: ReactNode;
}

export function ComFlag({ chave, children, alternativa = null }: ComFlagProps) {
  return <>{useFeatureFlag(chave) ? children : alternativa}</>;
}
